/**
 * Client-side PII (Personally Identifiable Information) detection and redaction.
 * Fast regex-based scanner — no network calls required.
 */

export interface PIIDetectionResult {
  hasPII: boolean;
  detectedTypes: string[];
  redactedContent: string;
}

interface PIIPattern {
  name: string;
  label: string;
  pattern: RegExp;
  replacement: string;
  critical: boolean; // If true, "Post Anyway" is hidden
}

const PII_PATTERNS: PIIPattern[] = [
  {
    name: 'ssn',
    label: 'Social Security Number',
    pattern: /\b\d{3}-\d{2}-\d{4}\b/g,
    replacement: '[SSN redacted]',
    critical: true,
  },
  {
    name: 'email',
    label: 'email address',
    pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    replacement: '[email redacted]',
    critical: false,
  },
  {
    name: 'phone',
    label: 'phone number',
    pattern: /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
    replacement: '[phone redacted]',
    critical: false,
  },
  {
    name: 'social_handle',
    label: 'social media handle',
    pattern: /\b(instagram|snapchat|twitter|tiktok|facebook|snap|ig|sc|fb)[\s:@]+@?[\w.]{2,}/gi,
    replacement: '[social handle redacted]',
    critical: false,
  },
  {
    name: 'address',
    label: 'physical address',
    pattern: /\b\d{1,5}\s+[\w\s]{2,30}\b(street|st|avenue|ave|road|rd|drive|dr|lane|ln|blvd|boulevard|court|ct|way|place|pl)\b/gi,
    replacement: '[address redacted]',
    critical: false,
  },
];

/**
 * Detect PII in text and return detection results with a redacted version.
 */
export function detectPII(text: string): PIIDetectionResult {
  const detectedTypes: string[] = [];
  let redactedContent = text;

  for (const { name, label, pattern, replacement } of PII_PATTERNS) {
    // Reset lastIndex for global regexes
    pattern.lastIndex = 0;
    if (pattern.test(text)) {
      detectedTypes.push(label);
      // Reset again before replace
      pattern.lastIndex = 0;
      redactedContent = redactedContent.replace(pattern, replacement);
    }
  }

  return {
    hasPII: detectedTypes.length > 0,
    detectedTypes,
    redactedContent,
  };
}

/**
 * Whether the detected PII includes critical types (SSN etc.)
 * that should NOT allow "Post Anyway".
 */
export function hasCriticalPII(text: string): boolean {
  return PII_PATTERNS.filter((p) => p.critical).some((p) => {
    p.pattern.lastIndex = 0;
    return p.pattern.test(text);
  });
}
